(function () {
	'use strict';

	const tagName = 'syk-quantity-input';
	const maximumPrecision = 8;

	if (!('customElements' in window) || window.customElements.get(tagName)) {
		return;
	}

	function toFiniteNumber(value) {
		if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
			return null;
		}

		const number = Number(value);
		return Number.isFinite(number) ? number : null;
	}

	function getDecimalPlaces(value) {
		if (value === null || value === undefined || value === '') {
			return 0;
		}

		const text = String(value).trim().toLowerCase();
		const match = text.match(/^[-+]?(?:\d+\.?\d*|\.\d+)(?:e([-+]?\d+))?$/);

		if (!match) {
			return 0;
		}

		const exponentIndex = text.indexOf('e');
		const mantissa = exponentIndex === -1 ? text : text.slice(0, exponentIndex);
		const decimalIndex = mantissa.indexOf('.');
		const decimals = decimalIndex === -1 ? 0 : mantissa.length - decimalIndex - 1;
		const exponent = match[1] ? parseInt(match[1], 10) : 0;

		return Math.min(maximumPrecision, Math.max(0, decimals - exponent));
	}

	function getPrecision(values) {
		let precision = 0;

		for (const value of values) {
			precision = Math.max(precision, getDecimalPlaces(value));
		}

		return Math.min(maximumPrecision, precision);
	}

	function formatValue(value, precision) {
		if (precision <= 0) {
			return String(Math.round(value));
		}

		return value.toFixed(precision).replace(/(\.\d*?[1-9])0+$|\.0+$/, '$1');
	}

	class SykQuantityInput extends HTMLElement {
		constructor() {
			super();
			this.input = null;
			this.semanticAmount = null;
			this.incrementControl = null;
			this.decrementControl = null;
			this.dispatchingChange = false;
			this.handleIncrementClick = this.handleIncrementClick.bind(this);
			this.handleDecrementClick = this.handleDecrementClick.bind(this);
			this.handleIncrementKeydown = this.handleIncrementKeydown.bind(this);
			this.handleDecrementKeydown = this.handleDecrementKeydown.bind(this);
			this.handleNativeEvent = this.handleNativeEvent.bind(this);
		}

		connectedCallback() {
			this.removeListeners();

			const input = this.findInput();

			if (!input) {
				this.clearReferences();
				return;
			}

			const semanticAmount = this.ensureSemanticAmount(input);

			if (!semanticAmount) {
				this.clearReferences();
				return;
			}

			const controls = this.ensureControls(semanticAmount);

			if (!controls) {
				this.clearReferences();
				return;
			}

			this.input = input;
			this.semanticAmount = semanticAmount;
			this.incrementControl = controls.incrementControl;
			this.decrementControl = controls.decrementControl;
			this.applyContextSize();
			this.updateControlLabels();
			this.addListeners();
			this.normalizeLocalValue(false);
		}

		disconnectedCallback() {
			this.removeListeners();
			this.clearReferences();
		}

		findInput() {
			const inputs = [];

			for (const input of this.querySelectorAll('input[type="number"]')) {
				if (input.closest(tagName) === this) {
					inputs.push(input);
				}
			}

			return inputs.length === 1 ? inputs[0] : null;
		}

		ensureSemanticAmount(input) {
			let semanticAmount = null;

			for (const child of this.children) {
				if (!child.classList.contains('semantic-amount')) {
					continue;
				}

				if (semanticAmount) {
					return null;
				}

				semanticAmount = child;
			}

			if (semanticAmount) {
				return semanticAmount.contains(input) ? semanticAmount : null;
			}

			semanticAmount = document.createElement('span');
			semanticAmount.className = 'semantic-amount';

			while (this.firstChild) {
				semanticAmount.appendChild(this.firstChild);
			}

			this.appendChild(semanticAmount);
			return semanticAmount;
		}

		findExistingControl(semanticAmount, className) {
			let control = false;

			for (const child of semanticAmount.children) {
				if (!child.classList.contains(className)) {
					continue;
				}

				if (control) {
					return null;
				}

				control = child;
			}

			if (control && control.tagName.toLowerCase() !== 'a') {
				return null;
			}

			return control;
		}

		createControl(semanticAmount, className) {
			const control = document.createElement('a');
			control.className = className;
			control.setAttribute('data-syk-quantity-control', className);
			semanticAmount.appendChild(control);
			return control;
		}

		prepareControl(control) {
			control.setAttribute('href', '#');
			control.setAttribute('role', 'button');
			return control;
		}

		ensureControls(semanticAmount) {
			let incrementControl = this.findExistingControl(semanticAmount, 'incr');
			let decrementControl = this.findExistingControl(semanticAmount, 'decr');

			if (incrementControl === null || decrementControl === null) {
				return null;
			}

			if (incrementControl && decrementControl && incrementControl === decrementControl) {
				return null;
			}

			if (!incrementControl) {
				incrementControl = this.createControl(semanticAmount, 'incr');
			}

			if (!decrementControl) {
				decrementControl = this.createControl(semanticAmount, 'decr');
			}

			return {
				incrementControl: this.prepareControl(incrementControl),
				decrementControl: this.prepareControl(decrementControl)
			};
		}

		applyContextSize() {
			const parent = this.parentElement;

			if (!parent || !parent.classList.contains('submit')) {
				return;
			}

			if (parent.querySelector('.size-m')) {
				this.classList.add('size-m');
			}

			if (parent.querySelector('.size-l')) {
				this.classList.add('size-l');
			}
		}

		updateControlLabels() {
			const label = this.getLocalLabel();
			const labelText = label ? label.textContent.trim() : '';
			const fallbackLabel = this.input.getAttribute('aria-label');
			const quantityLabel = labelText || (fallbackLabel ? fallbackLabel.trim() : '');

			this.incrementControl.setAttribute('aria-label', quantityLabel ? quantityLabel + ' +' : '+');
			this.decrementControl.setAttribute('aria-label', quantityLabel ? quantityLabel + ' -' : '-');
		}

		getLocalLabel() {
			if (!this.input || !this.input.labels) {
				return null;
			}

			for (const label of this.input.labels) {
				if (label.closest(tagName) === this) {
					return label;
				}
			}

			return null;
		}

		addListeners() {
			this.incrementControl.addEventListener('click', this.handleIncrementClick);
			this.decrementControl.addEventListener('click', this.handleDecrementClick);
			this.incrementControl.addEventListener('keydown', this.handleIncrementKeydown);
			this.decrementControl.addEventListener('keydown', this.handleDecrementKeydown);
			this.input.addEventListener('input', this.handleNativeEvent);
			this.input.addEventListener('change', this.handleNativeEvent);
		}

		removeListeners() {
			if (this.incrementControl) {
				this.incrementControl.removeEventListener('click', this.handleIncrementClick);
				this.incrementControl.removeEventListener('keydown', this.handleIncrementKeydown);
			}

			if (this.decrementControl) {
				this.decrementControl.removeEventListener('click', this.handleDecrementClick);
				this.decrementControl.removeEventListener('keydown', this.handleDecrementKeydown);
			}

			if (this.input) {
				this.input.removeEventListener('input', this.handleNativeEvent);
				this.input.removeEventListener('change', this.handleNativeEvent);
			}
		}

		clearReferences() {
			this.input = null;
			this.semanticAmount = null;
			this.incrementControl = null;
			this.decrementControl = null;
			this.dispatchingChange = false;
		}

		handleIncrementClick(event) {
			event.preventDefault();
			this.activate(1, this.incrementControl);
		}

		handleDecrementClick(event) {
			event.preventDefault();
			this.activate(-1, this.decrementControl);
		}

		handleIncrementKeydown(event) {
			this.handleControlKeydown(event, 1, this.incrementControl);
		}

		handleDecrementKeydown(event) {
			this.handleControlKeydown(event, -1, this.decrementControl);
		}

		handleControlKeydown(event, direction, control) {
			if (event.key !== ' ' && event.key !== 'Spacebar') {
				return;
			}

			event.preventDefault();

			if (!event.repeat) {
				this.activate(direction, control);
			}
		}

		handleNativeEvent() {
			if (this.dispatchingChange) {
				return;
			}

			this.normalizeLocalValue(true);
		}

		activate(direction, control) {
			if (!this.input || !control || this.isControlDisabled(control)) {
				return;
			}

			const state = this.readState(this.input.value);

			if (!state.validRange || this.input.disabled) {
				this.updateControlStates(state);
				return;
			}

			const nextIndex = Math.max(0, Math.min(state.maxIndex, state.index + direction));

			if (nextIndex === state.index) {
				this.updateControlStates(state);
				return;
			}

			const nextValue = this.valueFromIndex(state, nextIndex);
			this.setLocalValue(nextValue, state.precision);
			this.synchronizePeers(nextValue);
			this.updateControlStates(this.readState(this.input.value));
			this.dispatchingChange = true;

			try {
				this.input.dispatchEvent(new Event('change', {
					bubbles: true
				}));
			} finally {
				this.dispatchingChange = false;
			}
		}

		normalizeLocalValue(synchronizePeers) {
			if (!this.input) {
				return;
			}

			const state = this.readState(this.input.value);

			if (!state.validRange) {
				this.updateControlStates(state);
				return;
			}

			this.setLocalValue(state.value, state.precision);

			if (synchronizePeers) {
				this.synchronizePeers(state.value);
			}

			this.updateControlStates(this.readState(this.input.value));
		}

		readState(rawValue) {
			const minAttribute = this.input.getAttribute('min');
			const maxAttribute = this.input.getAttribute('max');
			const stepAttribute = this.input.getAttribute('step');
			const parsedMin = toFiniteNumber(minAttribute);
			const parsedMax = toFiniteNumber(maxAttribute);
			const parsedStep = toFiniteNumber(stepAttribute);
			const min = parsedMin === null ? 1 : parsedMin;
			const max = parsedMax === null ? Infinity : parsedMax;
			const step = parsedStep !== null && parsedStep > 0 ? parsedStep : 1;
			const precision = getPrecision([minAttribute, maxAttribute, stepAttribute, rawValue]);
			const scale = Math.pow(10, precision);
			const minScaled = Math.round(min * scale);
			const stepScaled = Math.max(1, Math.round(step * scale));
			const validRange = max >= min;
			let maxIndex = Infinity;

			if (validRange && Number.isFinite(max)) {
				const maxScaled = Math.floor(max * scale + 0.0000001);
				maxIndex = Math.max(0, Math.floor((maxScaled - minScaled) / stepScaled));
			}

			if (!validRange) {
				return {
					validRange: false,
					precision: precision,
					minScaled: minScaled,
					stepScaled: stepScaled,
					index: 0,
					maxIndex: -1,
					value: rawValue
				};
			}

			let numericValue = toFiniteNumber(rawValue);

			if (numericValue === null) {
				numericValue = min;
			}

			const candidateScaled = Math.round(numericValue * scale);
			let index = Math.round((candidateScaled - minScaled) / stepScaled);
			index = Math.max(0, Math.min(maxIndex, index));

			return {
				validRange: true,
				precision: precision,
				minScaled: minScaled,
				stepScaled: stepScaled,
				index: index,
				maxIndex: maxIndex,
				value: (minScaled + index * stepScaled) / scale
			};
		}

		valueFromIndex(state, index) {
			const scale = Math.pow(10, state.precision);
			return (state.minScaled + index * state.stepScaled) / scale;
		}

		setLocalValue(value, precision) {
			this.input.value = formatValue(value, precision);
		}

		synchronizePeers(value) {
			const group = this.getAttribute('data-quantity-group');

			if (!group) {
				return;
			}

			for (const peer of document.getElementsByTagName(tagName)) {
				if (peer === this || !peer.isConnected || peer.getAttribute('data-quantity-group') !== group) {
					continue;
				}

				peer.receivePeerValue(value);
			}
		}

		receivePeerValue(value) {
			if (!this.input) {
				return;
			}

			const state = this.readState(value);

			if (!state.validRange) {
				this.updateControlStates(state);
				return;
			}

			this.setLocalValue(state.value, state.precision);
			this.updateControlStates(this.readState(this.input.value));
		}

		updateControlStates(state) {
			if (!this.incrementControl || !this.decrementControl || !this.input) {
				return;
			}

			const unavailable = this.input.disabled || !state.validRange;
			const decrementDisabled = unavailable || state.index <= 0;
			const incrementDisabled = unavailable || state.index >= state.maxIndex;
			this.setControlDisabled(this.decrementControl, decrementDisabled);
			this.setControlDisabled(this.incrementControl, incrementDisabled);
		}

		setControlDisabled(control, disabled) {
			control.classList.toggle('disabled', disabled);
			control.setAttribute('aria-disabled', disabled ? 'true' : 'false');
		}

		isControlDisabled(control) {
			return control.classList.contains('disabled') || control.getAttribute('aria-disabled') === 'true';
		}
	}

	window.customElements.define(tagName, SykQuantityInput);
}());
